import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { useWizard } from '../../context/WizardContext';
import NavigationButtons from '../shared/NavigationButtons';
import { Download, Image, FileText, FileCode, LayoutList, GitBranch, Move, Save } from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const C7_ExportDiagram = () => {
  const { dataC, updateDataRaw, nextStep, previousStep } = useWizard();
  const diagramRef = useRef(null);
  const simpleViewCanvasRef = useRef(null);
  const [exporting, setExporting] = useState(false);
  const [diagramView, setDiagramView] = useState('linear');
  const [draggedNode, setDraggedNode] = useState(null);
  const [saveMessage, setSaveMessage] = useState('');
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  const nodes = dataC?.diagramNodes || [];
  const connections = dataC?.diagramConnections || [];
  const constructs = useMemo(() => dataC?.constructs || [], [dataC?.constructs]);

  const NODE_WIDTH = 150;
  const NODE_HEIGHT = 55;
  const CANVAS_HEIGHT = 450;

  const getDefaultPositions = useCallback(() => {
    const positions = {};
    const ivs = constructs.filter(c => c.type === 'independent');
    const dvs = constructs.filter(c => c.type === 'dependent');
    const meds = constructs.filter(c => c.type === 'mediator');
    const mods = constructs.filter(c => c.type === 'moderator');
    const ctrls = constructs.filter(c => c.type === 'control');
    
    const hasMeds = meds.length > 0;
    const colGap = hasMeds ? 200 : 280;
    
    ivs.forEach((c, i) => {
      positions[c.id] = { x: 30, y: 30 + i * 70 };
    });
    meds.forEach((c, i) => {
      positions[c.id] = { x: 30 + colGap, y: 30 + i * 70 };
    });
    dvs.forEach((c, i) => {
      positions[c.id] = { x: hasMeds ? 30 + colGap * 2 : 30 + colGap, y: 30 + i * 70 };
    });
    mods.forEach((c, i) => {
      positions[c.id] = { x: 200, y: 280 + i * 70 };
    });
    ctrls.forEach((c, i) => {
      positions[c.id] = { x: 400, y: 330 + i * 70 };
    });
    return positions;
  }, [constructs]);

  const getInitialPreviewPositions = useCallback(() => {
    const saved = dataC?.previewNodePositions;
    const defaults = getDefaultPositions();
    
    if (saved && Object.keys(saved).length > 0) {
      const merged = { ...defaults };
      Object.keys(saved).forEach(id => {
        if (constructs.some(c => c.id === id)) {
          merged[id] = saved[id];
        }
      });
      return merged;
    }
    return defaults;
  }, [constructs, dataC?.previewNodePositions, getDefaultPositions]);

  const [previewPositions, setPreviewPositions] = useState(() => getInitialPreviewPositions());
  const positionsRef = useRef(previewPositions);
  
  useEffect(() => {
    positionsRef.current = previewPositions;
  }, [previewPositions]);

  useEffect(() => {
    if (constructs.length > 0) {
      const positionIds = new Set(Object.keys(positionsRef.current));
      const allPresent = constructs.every(c => positionIds.has(c.id));
      
      if (!allPresent || positionIds.size === 0) {
        const newPositions = getInitialPreviewPositions();
        setPreviewPositions(newPositions);
        positionsRef.current = newPositions;
      }
    }
  // Only run when constructs change, not when positions change
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [constructs]);

  const handlePreviewMouseDown = (e, constructId) => {
    if (e.button === 0 && simpleViewCanvasRef.current) {
      const rect = simpleViewCanvasRef.current.getBoundingClientRect();
      const pos = previewPositions[constructId] || { x: 0, y: 0 };
      setDragOffset({
        x: e.clientX - rect.left - pos.x,
        y: e.clientY - rect.top - pos.y
      });
      setDraggedNode(constructId);
    }
  };

  const handlePreviewMouseMove = (e) => {
    if (draggedNode && simpleViewCanvasRef.current) {
      const rect = simpleViewCanvasRef.current.getBoundingClientRect();
      const newX = Math.max(0, Math.min(e.clientX - rect.left - dragOffset.x, rect.width - NODE_WIDTH));
      const newY = Math.max(0, Math.min(e.clientY - rect.top - dragOffset.y, rect.height - NODE_HEIGHT));
      
      const newPositions = {
        ...positionsRef.current,
        [draggedNode]: { x: newX, y: newY }
      };
      positionsRef.current = newPositions;
      setPreviewPositions(newPositions);
    }
  };

  const handlePreviewMouseUp = () => {
    if (draggedNode) {
      updateDataRaw({ 'wizardC.previewNodePositions': positionsRef.current });
      setDraggedNode(null);
      setSaveMessage('Saved!');
      setTimeout(() => setSaveMessage(''), 1500);
    }
  };

  const savePreviewArrangement = () => {
    updateDataRaw({ 'wizardC.previewNodePositions': positionsRef.current });
    setSaveMessage('Arrangement saved!');
    setTimeout(() => setSaveMessage(''), 2000);
  };


  const getTypeColor = (type) => {
    const colors = {
      independent: '#3b82f6',
      dependent: '#22c55e',
      mediator: '#f59e0b',
      moderator: '#8b5cf6',
      control: '#6b7280'
    };
    return colors[type] || '#6b7280';
  };

  const getNodeCenter = (constructId) => {
    let pos = previewPositions[constructId];
    if (!pos) {
      const defaults = getDefaultPositions();
      pos = defaults[constructId];
    }
    if (!pos) return { x: 0, y: 0 };
    return { x: pos.x + NODE_WIDTH / 2, y: pos.y + NODE_HEIGHT / 2 };
  };

  const exportAsPNG = async () => {
    if (!diagramRef.current) return;
    setExporting(true);
    try {
      const canvas = await html2canvas(diagramRef.current, {
        backgroundColor: '#ffffff',
        scale: 2
      });
      const link = document.createElement('a');
      link.download = 'conceptual-framework.png';
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (err) {
      console.error('Export failed:', err);
    }
    setExporting(false);
  };

  const exportAsPDF = async () => {
    if (!diagramRef.current) return;
    setExporting(true);
    try {
      const canvas = await html2canvas(diagramRef.current, {
        backgroundColor: '#ffffff',
        scale: 2
      });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'px',
        format: [canvas.width, canvas.height]
      });
      pdf.addImage(imgData, 'PNG', 0, 0, canvas.width, canvas.height);
      pdf.save('conceptual-framework.pdf');
    } catch (err) {
      console.error('Export failed:', err);
    }
    setExporting(false);
  };

  const exportAsJSON = () => {
    const data = {
      constructs: dataC?.constructs || [],
      levels: dataC?.levels || [],
      diagramNodes: nodes,
      diagramConnections: connections,
      assumptions: dataC?.assumptions || {},
      propositions: dataC?.propositions || [],
      exportedAt: new Date().toISOString()
    };
    
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const link = document.createElement('a');
    link.download = 'conceptual-framework.json';
    link.href = URL.createObjectURL(blob);
    link.click();
    URL.revokeObjectURL(link.href);
  };

  return (
    <div className="wizard-step">
      <div className="step-header">
        <div className="step-icon export-icon">
          <Download size={32} />
        </div>
        <div>
          <h2>Step 9: Export Conceptual Framework</h2>
          <p className="step-description">Download your conceptual framework in various formats</p>
        </div>
      </div>

      <div className="step-content">
        <div className="export-buttons">
          <button 
            className="export-btn png-btn" 
            onClick={exportAsPNG}
            disabled={exporting || nodes.length === 0}
          >
            <Image size={20} />
            <div>
              <span className="btn-title">Export as PNG</span>
              <span className="btn-desc">High-quality image</span>
            </div>
          </button>
          
          <button 
            className="export-btn pdf-btn"
            onClick={exportAsPDF}
            disabled={exporting || nodes.length === 0}
          >
            <FileText size={20} />
            <div>
              <span className="btn-title">Export as PDF</span>
              <span className="btn-desc">Print-ready document</span>
            </div>
          </button>
          
          <button 
            className="export-btn json-btn"
            onClick={exportAsJSON}
            disabled={nodes.length === 0}
          >
            <FileCode size={20} />
            <div>
              <span className="btn-title">Export as JSON</span>
              <span className="btn-desc">Editable format</span>
            </div>
          </button>
        </div>

        <div className="diagram-preview-container">
          <h3>Conceptual Framework Preview</h3>
          
          <div className="diagram-view-toggle">
            <button 
              className={`view-toggle-btn ${diagramView === 'linear' ? 'active' : ''}`}
              onClick={() => setDiagramView('linear')}
            >
              <LayoutList size={18} />
              Simple View
            </button>
            <button 
              className={`view-toggle-btn ${diagramView === 'logic' ? 'active' : ''}`}
              onClick={() => setDiagramView('logic')}
            >
              <GitBranch size={18} />
              Logic Flow
            </button>
          </div>
          
          <div 
            className="diagram-preview"
            ref={diagramRef}
          >
            {constructs.length === 0 && nodes.length === 0 ? (
              <div className="empty-preview">
                <p>No conceptual framework to export. Please go back to Step 1 to add constructs or Step 4 to create your conceptual framework.</p>
              </div>
            ) : diagramView === 'linear' ? (
              <div className="linear-framework-view">
                <div className="linear-framework-title">Conceptual Framework</div>
                
                <div className="simple-view-toolbar">
                  <div className="toolbar-hint">
                    <Move size={14} />
                    <span>Drag boxes to rearrange</span>
                  </div>
                  <div className="toolbar-actions">
                    <button 
                      className="save-btn"
                      onClick={savePreviewArrangement}
                      title="Save arrangement"
                    >
                      <Save size={16} />
                      Save Arrangement
                    </button>
                    {saveMessage && <span className="save-message-inline">{saveMessage}</span>}
                  </div>
                </div>

                {connections.length === 0 ? (
                  <div className="no-connections-warning">
                    <p>No relationships defined yet. Please go to Step 3: Map Relationships to connect your constructs.</p>
                  </div>
                ) : null}

                <div 
                  className="simple-view-canvas"
                  ref={simpleViewCanvasRef}
                  onMouseMove={handlePreviewMouseMove}
                  onMouseUp={handlePreviewMouseUp}
                  onMouseLeave={handlePreviewMouseUp}
                  style={{ height: `${CANVAS_HEIGHT}px` }}
                >
                  <svg className="simple-view-connections" width="100%" height="100%">
                    <defs>
                      <marker id="arrowhead-simple" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                        <polygon points="0 0, 10 3.5, 0 7" fill="#64748b" />
                      </marker>
                    </defs>
                    {connections.map((conn, i) => {
                      const fromPos = getNodeCenter(conn.from);
                      const toPos = getNodeCenter(conn.to);
                      if (!fromPos.x && !fromPos.y) return null;
                      if (!toPos.x && !toPos.y) return null;
                      
                      const fromConstruct = constructs.find(c => c.id === conn.from);
                      const isModerated = conn.type === 'moderated' || fromConstruct?.type === 'moderator';
                      const arrowColor = isModerated ? '#8b5cf6' : fromConstruct?.type === 'control' ? '#6b7280' : '#64748b';
                      
                      return (
                        <line
                          key={`conn-${i}`}
                          x1={fromPos.x}
                          y1={fromPos.y}
                          x2={toPos.x}
                          y2={toPos.y}
                          stroke={arrowColor}
                          strokeWidth="2"
                          strokeDasharray={isModerated ? '5,5' : 'none'}
                          markerEnd="url(#arrowhead-simple)"
                        />
                      );
                    })}
                  </svg>

                  {constructs.map((c) => {
                    const pos = previewPositions[c.id] || { x: 0, y: 0 };
                    const typeLabels = {
                      independent: 'Independent Variable',
                      dependent: 'Dependent Variable',
                      mediator: 'Mediator',
                      moderator: 'Moderator',
                      control: 'Control Variable'
                    };
                    return (
                      <div
                        key={c.id}
                        className={`simple-view-node ${draggedNode === c.id ? 'dragging' : ''}`}
                        style={{
                          left: pos.x,
                          top: pos.y,
                          backgroundColor: getTypeColor(c.type),
                          width: `${NODE_WIDTH}px`,
                          height: `${NODE_HEIGHT}px`
                        }}
                        onMouseDown={(e) => handlePreviewMouseDown(e, c.id)}
                      >
                        <div className="simple-node-name">{c.name?.substring(0, 18) || 'Unnamed'}</div>
                        <div className="simple-node-type">({typeLabels[c.type]})</div>
                      </div>
                    );
                  })}
                </div>
                
                <div className="simple-framework-legend">
                  <span className="legend-item"><span className="legend-dot" style={{background: '#3b82f6'}}></span> Independent</span>
                  <span className="legend-item"><span className="legend-dot" style={{background: '#f59e0b'}}></span> Mediator</span>
                  <span className="legend-item"><span className="legend-dot" style={{background: '#22c55e'}}></span> Dependent</span>
                  <span className="legend-item"><span className="legend-dot" style={{background: '#8b5cf6'}}></span> Moderator</span>
                  <span className="legend-item"><span className="legend-dot" style={{background: '#6b7280'}}></span> Control</span>
                </div>
              </div>
            ) : (
              <div className="logic-flow-view">
                <div className="logic-flow-title">Conceptual Framework Logic Flow</div>
                
                <svg className="logic-flow-svg" width="100%" height="550" viewBox="0 0 800 550">
                  <defs>
                    <marker id="arrow-logic" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                      <polygon points="0 0, 10 3.5, 0 7" fill="#64748b" />
                    </marker>
                  </defs>
                  
                  <rect x="300" y="20" width="200" height="50" rx="8" fill="#3b82f6" />
                  <text x="400" y="50" textAnchor="middle" fill="white" fontSize="14" fontWeight="bold">Research Context</text>
                  
                  <line x1="400" y1="70" x2="400" y2="100" stroke="#64748b" strokeWidth="2" markerEnd="url(#arrow-logic)" />
                  
                  <polygon points="400,110 480,155 400,200 320,155" fill="#f59e0b" stroke="#d97706" strokeWidth="2" />
                  <text x="400" y="160" textAnchor="middle" fill="white" fontSize="12" fontWeight="bold">Has IVs?</text>
                  
                  <line x1="320" y1="155" x2="180" y2="155" stroke="#64748b" strokeWidth="2" />
                  <text x="250" y="145" textAnchor="middle" fill="#64748b" fontSize="11">No</text>
                  <rect x="50" y="135" width="130" height="40" rx="6" fill="#ef4444" />
                  <text x="115" y="160" textAnchor="middle" fill="white" fontSize="12">Define Variables</text>
                  
                  <line x1="400" y1="200" x2="400" y2="230" stroke="#64748b" strokeWidth="2" markerEnd="url(#arrow-logic)" />
                  <text x="415" y="220" fill="#64748b" fontSize="11">Yes</text>
                  
                  <rect x="250" y="240" width="300" height="60" rx="8" fill="#3b82f6" />
                  <text x="400" y="265" textAnchor="middle" fill="white" fontSize="13" fontWeight="bold">
                    {constructs.filter(c => c.type === 'independent').length > 0 
                      ? constructs.filter(c => c.type === 'independent').map(c => c.name).join(', ').substring(0, 40) + (constructs.filter(c => c.type === 'independent').map(c => c.name).join(', ').length > 40 ? '...' : '')
                      : 'Independent Variables'}
                  </text>
                  <text x="400" y="285" textAnchor="middle" fill="rgba(255,255,255,0.8)" fontSize="11">(Independent Variables)</text>
                  
                  <line x1="400" y1="300" x2="400" y2="330" stroke="#64748b" strokeWidth="2" markerEnd="url(#arrow-logic)" />
                  
                  <polygon points="400,340 480,385 400,430 320,385" fill="#8b5cf6" stroke="#7c3aed" strokeWidth="2" />
                  <text x="400" y="380" textAnchor="middle" fill="white" fontSize="12" fontWeight="bold">Mediator?</text>
                  <text x="400" y="395" textAnchor="middle" fill="rgba(255,255,255,0.8)" fontSize="10">Effects</text>
                  
                  <line x1="480" y1="385" x2="580" y2="385" stroke="#64748b" strokeWidth="2" />
                  <text x="530" y="375" textAnchor="middle" fill="#64748b" fontSize="11">Yes</text>
                  <rect x="580" y="360" width="180" height="50" rx="6" fill="#f59e0b" />
                  <text x="670" y="390" textAnchor="middle" fill="white" fontSize="12">
                    {constructs.filter(c => c.type === 'mediator').length > 0 
                      ? constructs.filter(c => c.type === 'mediator')[0]?.name?.substring(0, 20) + (constructs.filter(c => c.type === 'mediator')[0]?.name?.length > 20 ? '...' : '') || 'Mediator'
                      : 'Mediator'}
                  </text>
                  <line x1="670" y1="410" x2="670" y2="490" stroke="#64748b" strokeWidth="2" />
                  <line x1="670" y1="490" x2="400" y2="490" stroke="#64748b" strokeWidth="2" markerEnd="url(#arrow-logic)" />
                  
                  <line x1="320" y1="385" x2="220" y2="385" stroke="#64748b" strokeWidth="2" />
                  <text x="270" y="375" textAnchor="middle" fill="#64748b" fontSize="11">No</text>
                  <line x1="220" y1="385" x2="220" y2="490" stroke="#64748b" strokeWidth="2" />
                  <line x1="220" y1="490" x2="300" y2="490" stroke="#64748b" strokeWidth="2" />
                  
                  <rect x="300" y="465" width="200" height="60" rx="8" fill="#22c55e" />
                  <text x="400" y="490" textAnchor="middle" fill="white" fontSize="13" fontWeight="bold">
                    {constructs.filter(c => c.type === 'dependent').length > 0 
                      ? constructs.filter(c => c.type === 'dependent')[0]?.name?.substring(0, 25) + (constructs.filter(c => c.type === 'dependent')[0]?.name?.length > 25 ? '...' : '') || 'DV'
                      : 'Dependent Variable'}
                  </text>
                  <text x="400" y="510" textAnchor="middle" fill="rgba(255,255,255,0.8)" fontSize="11">(Outcome)</text>
                  
                  {constructs.filter(c => c.type === 'moderator').length > 0 && (
                    <>
                      <rect x="580" y="245" width="180" height="50" rx="6" fill="#8b5cf6" opacity="0.9" />
                      <text x="670" y="275" textAnchor="middle" fill="white" fontSize="12">
                        {constructs.filter(c => c.type === 'moderator')[0]?.name?.substring(0, 20) + (constructs.filter(c => c.type === 'moderator')[0]?.name?.length > 20 ? '...' : '') || 'Moderator'}
                      </text>
                      <line x1="580" y1="270" x2="550" y2="270" stroke="#8b5cf6" strokeWidth="2" strokeDasharray="5,3" />
                    </>
                  )}
                </svg>
                
                <div className="logic-flow-legend">
                  <div className="legend-item"><span className="legend-box" style={{background: '#3b82f6'}}></span> Variables</div>
                  <div className="legend-item"><span className="legend-box diamond" style={{background: '#f59e0b'}}></span> Decision Points</div>
                  <div className="legend-item"><span className="legend-box" style={{background: '#22c55e'}}></span> Outcomes</div>
                  <div className="legend-item"><span className="legend-box" style={{background: '#8b5cf6'}}></span> Moderators</div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="tip-box">
          <strong>Tip:</strong> Use PNG for presentations and documents. Use PDF for printing. 
          Use JSON to save your work and import it later for further editing.
        </div>
      </div>

      <NavigationButtons
        onBack={previousStep}
        onContinue={nextStep}
        canContinue={true}
        continueLabel="Continue to Framework Section"
      />
    </div>
  );
};

export default C7_ExportDiagram;
